import { logSupabaseException } from "@punchup/sentry";
import {
  CollectionInsert,
  CollectionRow,
  CollectionWithCountAndFeaturedSpecial,
} from "../../models/collections";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import dayjs from "@punchup/utils/dayjs";

/**
 * Adds a special to a collection with validation
 * @param collection_id The collection to add the special to
 * @param special_id The special to add
 * @param comedian_id The comedian who owns the special (for validation)
 * @param is_featured Whether this special should be featured in the collection
 * @returns void
 * @throws Error if collection not found or comedian doesn't own the collection
 */
export async function getManagerCollection(
  collectionId: string
): Promise<CollectionWithCountAndFeaturedSpecial | null> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("collections")
    .select("*, collection_specials(count)")
    .eq("id", collectionId)
    .maybeSingle();

  if (error) {
    logSupabaseException(error, "Failed to fetch manager collection");
    throw error;
  }

  if (!data) return null;

  const { collection_specials, ...rest } = data as CollectionRow & {
    collection_specials: { count: number }[];
  };

  return {
    ...rest,
    specials_count: collection_specials?.[0]?.count ?? 0,
    featured_special: null,
  };
}

export async function addSpecialToCollection({
  collection_id,
  special_id,
  comedian_id,
  is_featured = false,
}: {
  collection_id: string;
  special_id: string;
  comedian_id: string;
  is_featured?: boolean;
}): Promise<void> {
  const supabase = createSupabaseAdminClient();

  const collection = await getManagerCollection(collection_id);

  if (!collection) {
    throw new Error(`Collection ${collection_id} not found`);
  }

  if (collection.comedian_id !== comedian_id) {
    throw new Error(
      `Comedian ${comedian_id} does not own collection ${collection_id}`
    );
  }

  await supabase
    .from("collection_specials")
    .insert({
      collection_id,
      special_id,
      sort_order: collection.specials_count + 1,
      is_featured,
    })
    .throwOnError();
}

/**
 * Fetch collections owned by a comedian. Only returns collections with kind="collection"
 * (excludes podcasts). Set allowUnpublished=true to include drafts.
 */
export async function getManagerCollections(
  {
    comedian_id,
    collection_id,
    slug,
  }: {
    comedian_id?: string;
    collection_id?: string;
    slug?: string;
  },
  allowUnpublished = false
): Promise<CollectionRow[]> {
  const supabase = createSupabaseAdminClient();

  let query = supabase.from("collections").select("*").eq("kind", "collection");

  if (comedian_id) query = query.eq("comedian_id", comedian_id);
  if (collection_id) query = query.eq("id", collection_id);
  if (slug) query = query.eq("slug", slug);
  if (!allowUnpublished) {
    query = query.lte("published_at", dayjs().toISOString());
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) {
    logSupabaseException(error, "Failed to fetch manager collections");
    throw error;
  }

  return data;
}

export async function updateCollection(
  collectionId: string,
  updates: {
    name?: string;
    slug?: string;
    description?: string;
    requires_subscription?: boolean;
    requires_purchase?: boolean;
    subscription_product_id?: string | null;
    required_subscription_tier?: number | null;
  }
): Promise<CollectionRow> {
  const supabase = createSupabaseAdminClient();

  const fields: Record<string, unknown> = {};
  if (updates.name != null) fields.name = updates.name;
  if (updates.slug != null) fields.slug = updates.slug;
  if (updates.description != null) fields.description = updates.description;
  if (updates.requires_subscription != null)
    fields.requires_subscription = updates.requires_subscription;
  if (updates.requires_purchase != null)
    fields.requires_purchase = updates.requires_purchase;
  if (updates.subscription_product_id !== undefined)
    fields.subscription_product_id = updates.subscription_product_id;
  if (updates.required_subscription_tier !== undefined)
    fields.required_subscription_tier = updates.required_subscription_tier;

  const { data, error } = await supabase
    .from("collections")
    .update(fields)
    .eq("id", collectionId)
    .select()
    .single();

  if (error) {
    logSupabaseException(error, "Failed to update collection");
    throw error;
  }

  return data;
}

export async function publishCollection(
  collectionId: string
): Promise<CollectionRow> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("collections")
    .update({ published_at: dayjs().toISOString() })
    .eq("id", collectionId)
    .select()
    .single();

  if (error) {
    logSupabaseException(error, "Failed to publish collection");
    throw error;
  }

  return data;
}

export async function removeSpecialFromCollection({
  collection_id,
  special_id,
}: {
  collection_id: string;
  special_id: string;
}): Promise<void> {
  const supabase = createSupabaseAdminClient();

  const { error } = await supabase
    .from("collection_specials")
    .delete()
    .eq("collection_id", collection_id)
    .eq("special_id", special_id);

  if (error) {
    logSupabaseException(error, "Failed to remove special from collection");
    throw error;
  }
}

export async function createCollection(collection: CollectionInsert) {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("collections")
    .insert(collection)
    .select()
    .single();

  if (error) {
    logSupabaseException(error, "Failed to create collection");
    throw error;
  }

  return data;
}

export async function addSpecialsToCollection(
  collectionId: string,
  specialIds: string[]
): Promise<void> {
  if (specialIds.length === 0) return;

  const supabase = createSupabaseAdminClient();

  const { data: existing, error: fetchError } = await supabase
    .from("collection_specials")
    .select("special_id, sort_order")
    .eq("collection_id", collectionId);

  if (fetchError) {
    logSupabaseException(fetchError, "Failed to fetch collection specials");
    throw fetchError;
  }

  const existingIds = new Set((existing ?? []).map((r) => r.special_id));
  const newSpecialIds = specialIds.filter((id) => !existingIds.has(id));
  if (newSpecialIds.length === 0) return;

  // Concurrent calls can read the same max and produce duplicate sort_order values.
  // Data integrity is preserved (upsert with ignoreDuplicates), but ordering may be
  // unpredictable if two requests race. Acceptable: sort_order is user-adjustable
  // and single-comedian concurrent adds are rare in practice.
  const maxSortOrder = Math.max(
    0,
    ...(existing ?? []).map((r) => r.sort_order)
  );

  const inserts = newSpecialIds.map((specialId, index) => ({
    collection_id: collectionId,
    special_id: specialId,
    sort_order: maxSortOrder + index + 1,
    is_featured: false,
  }));

  const { error } = await supabase.from("collection_specials").upsert(inserts, {
    onConflict: "collection_id,special_id",
    ignoreDuplicates: true,
  });
  if (error) {
    logSupabaseException(error, "Failed to add specials to collection");
    throw error;
  }
}
